import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import { ReactComponent as FilterIcon } from '../assets/icons/main/filter-icon.svg';
import SearchField from './searchField';
import CustomDataTable from '../components/CustomDataTable';
import { palette } from '../theme/palette';
import { ReactComponent as CloseIcon } from '../assets/icons/main/close-icon.svg';
import { ReactComponent as WarnIcon } from '../assets/icons/main/alert-small-icon.svg';
import CheckboxComponent from '../components/Checkbox';


const Styles = {
  dialog: {
    muiPaper: {
      maxWidth: '800px',
      // padding: '21px 32px 32px',
      borderRadius: '16px',
    },
    // '& .MuiPaper-root:not(.MuiAccordion-root)': {
    // maxWidth: '800px',
    // padding: '21px 32px',
    // width: '800px',
    // borderRadius: '18px',
    // },
    // '&.MuiPaper-root.MuiDialog-root': {
    //   maxWidth: '800px',
    //   padding: '21px 32px',
    //   width: '800px',
    //   borderRadius: '18px',
    //   backgroundColor: `${palette.transparent.main}`,
    // },
  },
  dialogTitle: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '1rem',
    padding: '0px',
    fontFamily: 'Open Sans',
    fontWeight: '400',
  },
  content: {
    '&.MuiDialogContent-root': {
      padding: '0px',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '0px',
      gap: '0px',
      flex: 0
    },
  },
  closeIcon: {
    cursor: 'pointer',
  },
};

type ScanStatus = 'Not Started' | 'In Progress' | 'Completed' | 'Failed';

const UI_SCAN_STATUS_ORDER: ScanStatus[] = ['In Progress', 'Failed', 'Completed', 'Not Started'];

const UI_SCAN_STATUS_LABEL: Record<ScanStatus, string> = {
  'In Progress': 'In-progress',
  Failed: 'Failed',
  Completed: 'Completed',
  'Not Started': 'Not Started',
};

type ScanRow = {
  assetIndex: number;
  assetName: string;
  assetIp: string;
  warningCount: number;
  errorCount: number;
  status: ScanStatus;
};

type FlowImportScanModalProps = {
  open: boolean;
  assets: any[];
  scanAssetIndexes?: number[];
  selectedGatewayId: string | null;
  onScanComponent: (args: {
    gatewayId: string;
    asset: any;
    component: any;
    assetIndex: number;
    componentIndex: number;
    onStarted?: () => void;
    registerCancel?: (cancelFn: () => void) => void;
  }) => Promise<{ status: 'success' | 'warning' | 'failed' }>;
  onClose: () => void;
  onContinue: (payload: FlowImportScanContinuePayload) => void;
};

type FlowImportScanContinuePayload = {
  assets: any[];
  retainedAssetIndexes: number[];
  failedAssetIndexes: number[];
};

const statusChipColor = (status: ScanStatus): 'default' | 'success' | 'warning' | 'error' => {
  if (status === 'Completed') return 'success';
  if (status === 'In Progress') return 'warning';
  if (status === 'Failed') return 'error';
  return 'default';
};

const statusChipSx = (status: ScanStatus) => {
  if (status === 'Completed') {
    return {
      color: palette.chipSuccess.dark,
      borderColor: palette.chipSuccess.dark,
      backgroundColor: palette.chipSuccess.light,
    };
  }
  if (status === 'Failed') {
    return {
      color: palette.error.dark,
      borderColor: palette.error.dark,
      backgroundColor: palette.error.light,
    };
  }
  if (status === 'In Progress') {
    return {
      color: palette.chipWarning.dark,
      borderColor: palette.chipWarning.dark,
      backgroundColor: palette.chipWarning.light,
    };
  }
  return {
    color: palette.info.main,
    borderColor: palette.info.main,
    backgroundColor: palette.info.contrastText,
  };
};

const deepClone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

const FlowImportScanModal = ({
  open,
  assets,
  scanAssetIndexes = [],
  selectedGatewayId,
  onScanComponent,
  onClose,
  onContinue,
}: FlowImportScanModalProps) => {
  const theme = useTheme();
  const [draftAssets, setDraftAssets] = useState<any[]>([]);
  const [rows, setRows] = useState<ScanRow[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [search, setSearch] = useState('');
  // const [statusFilters, setStatusFilters] = useState<ScanStatus[]>(UI_SCAN_STATUS_ORDER);
  const [statusFilters, setStatusFilters] = useState<ScanStatus[]>([]);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [includeFailedAssets, setIncludeFailedAssets] = useState(true);
  const autoScanStartedRef = useRef(false);
  const latestDraftAssetsRef = useRef<any[]>([]);
  const openCycleRef = useRef(0);
  const isScanningRef = useRef(false);
  const isCompleteRef = useRef(false);
  const scanControlRef = useRef<{
    cancelled: boolean;
    runId: number;
    cancelCurrent?: () => void;
  }>({
    cancelled: false,
    runId: 0,
    cancelCurrent: undefined,
  });
  const PAGE_SIZE = 8;

  const cancelActiveScan = useCallback(() => {
    scanControlRef.current.cancelled = true;
    scanControlRef.current.cancelCurrent?.();
    isScanningRef.current = false;
    setIsScanning(false);
  }, []);

  const runScan = useCallback(async (targetIndexes?: number[]) => {
    if (isScanningRef.current || !selectedGatewayId) return;

    isScanningRef.current = true;
    setIsScanning(true);
    const runId = scanControlRef.current.runId;
    scanControlRef.current.cancelled = false;
    const baseAssets =
      Array.isArray(latestDraftAssetsRef.current) && latestDraftAssetsRef.current.length > 0
        ? latestDraftAssetsRef.current
        : assets;

    const nextAssets = deepClone(baseAssets || []);
    const defaultTargets =
      Array.isArray(scanAssetIndexes) && scanAssetIndexes.length > 0
        ? scanAssetIndexes.map((idx) => Number(idx)).filter((idx) => Number.isFinite(idx) && idx >= 0)
        : nextAssets.map((_: any, idx: number) => idx);

    const effectiveTargets =
      Array.isArray(targetIndexes) && targetIndexes.length > 0 ? targetIndexes : defaultTargets;

    const targetAssetSet = new Set(effectiveTargets);

    setRows((prev) =>
      prev.map((row) =>
        targetAssetSet.has(row.assetIndex)
          ? { ...row, status: 'Not Started', warningCount: 0, errorCount: 0 }
          : row
      )
    );

    setIsComplete(false);
    isCompleteRef.current = false;

    for (let i = 0; i < nextAssets.length; i += 1) {
      if (scanControlRef.current.cancelled || scanControlRef.current.runId !== runId) break;
      if (!targetAssetSet.has(i)) continue;

      const asset = nextAssets[i];
      const options = Array.isArray(asset?.data?.options) ? asset.data.options : [];
      let warningCount = 0;
      let errorCount = 0;

      for (let j = 0; j < options.length; j += 1) {
        if (scanControlRef.current.cancelled || scanControlRef.current.runId !== runId) break;

        const component = options[j];
        const result = await onScanComponent({
          gatewayId: selectedGatewayId,
          asset,
          component,
          assetIndex: i,
          componentIndex: j,
          registerCancel: (cancelFn) => {
            if (scanControlRef.current.runId !== runId || scanControlRef.current.cancelled) {
              cancelFn();
              return;
            }
            scanControlRef.current.cancelCurrent = cancelFn;
          },
          onStarted: () => {
            if (scanControlRef.current.cancelled || scanControlRef.current.runId !== runId) return;
            setRows((prev) =>
              prev.map((row) => (row.assetIndex === i ? { ...row, status: 'In Progress' } : row))
            );
          },
        });

        scanControlRef.current.cancelCurrent = undefined;
        if (scanControlRef.current.cancelled || scanControlRef.current.runId !== runId) break;

        if (result.status === 'failed') {
          errorCount += 1;
        } else if (result.status === 'warning') {
          warningCount += 1;
        }
      }

      if (scanControlRef.current.cancelled || scanControlRef.current.runId !== runId) break;

      const finalStatus: ScanStatus =
        errorCount > 0 || warningCount > 0 ? 'Failed' : 'Completed';

      setRows((prev) =>
        prev.map((row) =>
          row.assetIndex === i
            ? {
              ...row,
              warningCount,
              errorCount,
              status: finalStatus,
            }
            : row
        )
      );
    }

    if (scanControlRef.current.cancelled || scanControlRef.current.runId !== runId) {
      isScanningRef.current = false;
      setIsScanning(false);
      return;
    }
    latestDraftAssetsRef.current = nextAssets;
    setDraftAssets(nextAssets);
    isScanningRef.current = false;
    isCompleteRef.current = true;
    setIsScanning(false);
    setIsComplete(true);
  }, [assets, onScanComponent, scanAssetIndexes, selectedGatewayId]);

  const handleCancelAndClose = useCallback(() => {
    cancelActiveScan();
    onClose();
  }, [cancelActiveScan, onClose]);

  React.useEffect(() => {
    if (!open) {
      autoScanStartedRef.current = false;
      return;
    }

    openCycleRef.current += 1;
    const openCycle = openCycleRef.current;
    scanControlRef.current.cancelled = false;
    scanControlRef.current.runId += 1;
    scanControlRef.current.cancelCurrent = undefined;
    setDraftAssets(deepClone(assets || []));
    latestDraftAssetsRef.current = deepClone(assets || []);
    // NEW: build target set for this open cycle
    const targetAssetSet = new Set(
      Array.isArray(scanAssetIndexes) && scanAssetIndexes.length > 0
        ? scanAssetIndexes.map((idx) => Number(idx)).filter((idx) => Number.isFinite(idx) && idx >= 0)
        : (assets || []).map((_: any, idx: number) => idx)
    );

    // CHANGED: non-target assets start as Completed
    const initialRows: ScanRow[] = (assets || []).map((asset: any, assetIndex: number) => ({
      assetIndex,
      assetName: String(asset?.data?.name ?? '').trim(),
      assetIp: String(asset?.data?.ip ?? '').trim(),
      warningCount: 0,
      errorCount: 0,
      status: targetAssetSet.has(assetIndex) ? 'Not Started' : 'Completed',
    }));
    setRows(initialRows);
    isScanningRef.current = false;
    isCompleteRef.current = false;
    setIsScanning(false);
    setIsComplete(false);
    setSearch('');
    // setStatusFilters(UI_SCAN_STATUS_ORDER);
    setStatusFilters([]);
    setIncludeFailedAssets(true);
    autoScanStartedRef.current = false;

    Promise.resolve().then(() => {
      if (!open || openCycleRef.current !== openCycle || autoScanStartedRef.current) return;
      autoScanStartedRef.current = true;
      void runScan();
    });
  }, [assets, open, runScan, scanAssetIndexes]);

  React.useEffect(() => {
    latestDraftAssetsRef.current = draftAssets;
  }, [draftAssets]);

  // const filteredRows = useMemo(() => {
  //   const q = search.trim().toLowerCase();
  //   return rows.filter((row) => {
  //     const statusOk = statusFilters.includes(row.status);
  //     if (!statusOk) return false;
  //     if (!q) return true;
  //     return [row.assetName, row.assetIp, row.status].join(' ').toLowerCase().includes(q);
  //   });
  // }, [rows, search, statusFilters]);
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    return rows.filter((row) => {
      const statusOk =
        statusFilters.length === 0 || statusFilters.includes(row.status);

      if (!statusOk) return false;
      if (!q) return true;

      return row.assetName.toLowerCase().includes(q);
    });
  }, [rows, search, statusFilters]);

  const tableColumns = useMemo(
    () => [
      {
        field: 'assetName',
        headerName: 'Asset Name',
        sortable: true,
        minWidth: 300,
        renderCell: ({ row }: { row: ScanRow }) => (
          <Typography variant='body2' sx={{ color: theme.palette.text.primary, fontSize: '1rem', fontWeight: 400 }}>
            {row.assetName || '-'}
          </Typography>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        sortable: true,
        width: 180,
        renderCell: ({ row }: { row: ScanRow }) => (
          <Chip
            size='small'
            color={statusChipColor(row.status)}
            label={UI_SCAN_STATUS_LABEL[row.status]}
            variant='outlined'
            sx={{
              height: 22,
              borderRadius: '12px',
              fontWeight: 500,
              fontSize: '0.75rem',
              ...statusChipSx(row.status),
            }}
          />
        ),
      },
    ],
    []
  );


  const summary = useMemo(() => {
    const completedCount = rows.filter((row) => row.status === 'Completed').length;
    const inProgressCount = rows.filter((row) => row.status === 'In Progress').length;
    const failedCount = rows.filter((row) => row.status === 'Failed').length;
    return { completedCount, inProgressCount, failedCount };
  }, [rows]);

  const formatCounter = (value: number) => String(value).padStart(2, '0');

  const failedAssetIndexes = useMemo(
    () => rows.filter((row) => row.status === 'Failed').map((row) => row.assetIndex),
    [rows]
  );

  const retainedAssetIndexes = useMemo(
    () => rows.filter((row) => row.status !== 'Failed').map((row) => row.assetIndex),
    [rows]
  );

  const successfulAssets = useMemo(
    () => retainedAssetIndexes.map((idx) => draftAssets[idx]).filter(Boolean),
    [draftAssets, retainedAssetIndexes]
  );

  const handleRescanFailed = useCallback(() => {
    if (isScanning || failedAssetIndexes.length === 0) return;
    scanControlRef.current.runId += 1;
    void runScan(failedAssetIndexes);
  }, [failedAssetIndexes, isScanning, runScan]);

  const assetsForMessageValidation = useMemo(() => {
    const failedSet = new Set(failedAssetIndexes);

    return (draftAssets || [])
      .map((asset, assetIndex) => {
        if (!includeFailedAssets && failedSet.has(assetIndex)) return null;

        const nextAsset = deepClone(asset);
        const options = Array.isArray(nextAsset?.data?.options) ? nextAsset.data.options : [];

        const hasScanResult = options.some((option: any) => {
          const scanResult = option?.data?.scan_result;
          return Array.isArray(scanResult) && scanResult.length > 0;
        });

        if (failedSet.has(assetIndex) || (options.length > 0 && !hasScanResult)) {
          nextAsset.data = { ...(nextAsset.data || {}), enabled: false };
        }

        return nextAsset;
      })
      .filter(Boolean);
  }, [draftAssets, failedAssetIndexes, includeFailedAssets]);

  const handleNext = useCallback(() => {
    onContinue({
      assets: assetsForMessageValidation,
      retainedAssetIndexes,
      failedAssetIndexes,
    });
  }, [assetsForMessageValidation, failedAssetIndexes, onContinue, retainedAssetIndexes]);

  const toggleStatusFilter = useCallback((status: ScanStatus) => {
    setStatusFilters((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  }, []);

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth='sm'
      PaperProps={{
        sx: {
          borderRadius: '14px',
          width: '800px',
          maxWidth: '800px',
          boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
          backgroundColor: '#F8FAFB' //theme.palette.background.paper,
        },
      }}
    >
      <DialogTitle sx={{ ...Styles.dialogTitle, color: theme.palette.text.primary, }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 2, flexWrap: 'wrap', padding: '24px', paddingBottom: '8px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant='subtitle1' sx={{ ...Styles.dialogTitle, padding: 0, color: theme.palette.text.primary }}>
              ASSETS SCAN
            </Typography>
            <Typography variant='caption' sx={{ color: theme.palette.text.secondary, mt: 0, display: 'block', fontWeight: 400 }}>
              Scanning asset components to identify and retrieve all available tags and data points for configuration and validation.
            </Typography>
          </Box>
          <IconButton size='small' onClick={handleCancelAndClose} sx={{ color: theme.palette.info.main }}>
            <CloseIcon style={{ ...Styles.closeIcon, color: theme.palette.custom.light }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, pt: 2, paddingTop: '8px', }}>
        <Box sx={{ borderRadius: '12px', boxShadow: '0px 0px 14px 0px #0000000D', p: 3, backgroundColor: '#FFFFFF' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1.5, mb: 3 }}>
            <Box sx={{ border: `1px solid ${theme.palette.customBorderColor.main}`, boxShadow: '0px 0px 14px 0px #0000000D', borderRadius: '18px', px: 1, py: 1.2, textAlign: 'center' }}>
              <Typography variant='caption' sx={{ fontSize: '1rem', fontWeight: 400, color: theme.palette.text.primary }}>Total Assets</Typography>
              <Typography sx={{ color: '#5C85FF', fontSize: '2.2rem', fontWeight: 500, lineHeight: 1 }}>{formatCounter(rows.length)}</Typography>
            </Box>
            <Box sx={{ border: `1px solid ${theme.palette.customBorderColor.main}`, boxShadow: '0px 0px 14px 0px #0000000D', borderRadius: '18px', px: 1, py: 1.2, textAlign: 'center' }}>
              <Typography variant='caption' sx={{ fontSize: '1rem', fontWeight: 400, color: theme.palette.text.primary }}>Scan In-progress</Typography>
              <Typography sx={{ color: palette.chipWarning.dark, fontSize: '2.2rem', fontWeight: 500, lineHeight: 1 }}>{formatCounter(summary.inProgressCount)}</Typography>
            </Box>
            <Box sx={{ border: `1px solid ${theme.palette.customBorderColor.main}`, boxShadow: '0px 0px 14px 0px #0000000D', borderRadius: '18px', px: 1, py: 1.2, textAlign: 'center' }}>
              <Typography variant='caption' sx={{ fontSize: '1rem', fontWeight: 400, color: theme.palette.text.primary }}>Scan Failed</Typography>
              <Typography sx={{ color: palette.error.main, fontSize: '2.2rem', fontWeight: 500, lineHeight: 1 }}>{formatCounter(summary.failedCount)}</Typography>
            </Box>
            <Box sx={{ border: `1px solid ${theme.palette.customBorderColor.main}`, boxShadow: '0px 0px 14px 0px #0000000D', borderRadius: '18px', px: 1, py: 1.2, textAlign: 'center' }}>
              <Typography variant='caption' sx={{ fontSize: '1rem', fontWeight: 400, color: theme.palette.text.primary }}>Scan Completed</Typography>
              <Typography sx={{ color: palette.success.dark, fontSize: '2.2rem', fontWeight: 500, lineHeight: 1 }}>{formatCounter(summary.completedCount)}</Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, mb: 0.5, alignItems: 'center' }}>
            <Box sx={{ width: '40%', minWidth: 300 }}>
              <SearchField
                searchPlaceholder='Search'
                defaultValue={search}
                onSearch={(val: string) => setSearch(val)}
                width='100%'
              />
            </Box>

            <Button
              variant='text'
              size='small'
              onClick={(e) => setFilterAnchorEl(e.currentTarget)}
              startIcon={<FilterIcon style={{ width: 24, height: 24, color: theme.palette.custom.light }} />}
              sx={{ color: theme.palette.custom.light, fontSize: '16px', textTransform: 'none', fontWeight: 600, minWidth: 'fit-content', px: 0 }}
            >
              Filter
            </Button>
            <Menu
              anchorEl={filterAnchorEl}
              open={Boolean(filterAnchorEl)}
              onClose={() => setFilterAnchorEl(null)}
              PaperProps={{
                sx: {
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '10px',
                  boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.08)',
                  minWidth: 120,
                },
              }}
            >
              {UI_SCAN_STATUS_ORDER.map((status) => {
                const active = statusFilters.includes(status);

                return (
                  <MenuItem
                    key={status}
                    onClick={() => toggleStatusFilter(status)}
                    sx={{

                      // color: active ? palette.success.dark : palette.info.main,
                      color: theme.palette.text.primary,
                      fontWeight: active ? 600 : 400,
                      fontSize: '0.85rem',
                      py: 0.6,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      // backgroundColor: active ? palette.success.light : 'transparent',
                      // '&:hover': {
                      //   backgroundColor: active ? palette.success.light : undefined,
                      // },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.5 }}>
                      <CheckboxComponent
                        checked={active}
                        handleCheckboxChange={() => { }}
                        style={{ p: 0, pointerEvents: 'none' }}
                      />
                    </Box>
                    <Box component='span'>{UI_SCAN_STATUS_LABEL[status]}</Box>
                  </MenuItem>
                );
              })}
            </Menu>
          </Box>

          <Box sx={{ maxHeight: '44vh', overflowY: 'auto', marginTop: '24px', border: `1px solid ${theme.palette.customBorderColor.main}` }}>
            <CustomDataTable
              rows={filteredRows}
              columns={tableColumns}
              rowHeight={42}
              headerHeight={42}
              noRowsLabel='No assets found.'
              getRowId={(row: any) => `scan-${row.assetIndex}`}
            />

          </Box>
          {failedAssetIndexes.length > 0 && (
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                gap: '10px',
                border: '1px solid #FF8800',
                backgroundColor: '#FF88001A',
                borderRadius: '4px',
                padding: '16px'
              }}
            >
              <Box> <WarnIcon /></Box>
              <Typography sx={{ fontSize: '1rem', fontWeight: 400, color: '#FF8800', }}>
                Some asset scans failed. If you continue, validation cannot be performed for the
                assets that were not successfully scanned. You can rescan them now or manually scan and configure them later from Factory Management.
              </Typography>
            </Box>
          )}
          {failedAssetIndexes.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5 }}>
              <CheckboxComponent
                checked={includeFailedAssets}
                handleCheckboxChange={() => setIncludeFailedAssets((prev) => !prev)}
                style={{ p: 0 }}
              />
              <Typography variant='body2' sx={{ ml: 1, color: theme.palette.text.primary }}>
                Continue with failed assets.
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, pt: 2, pb: 0.5, px: 2 }}>
          <Button variant='outlined' onClick={handleCancelAndClose} size='small' sx={{ minWidth: 92 }}>
            Cancel
          </Button>
          <Button
            variant='outlined'
            onClick={handleRescanFailed}
            disabled={isScanning || failedAssetIndexes.length === 0}
            size='small'
            sx={{ minWidth: 92 }}
          >
            Rescan
          </Button>
          <Button
            variant='contained'
            onClick={handleNext}
            disabled={!isComplete || isScanning || draftAssets.length === 0}
            size='small'
            sx={{ minWidth: 92 }}
          >
            Next
          </Button>
        </Box>
      </DialogContent>
    </Dialog >
  );
};

export default memo(FlowImportScanModal);
